// @ts-check

import {
  BOOKED,
  CANCELED,
  PENDING,
  REJECTED,
  RELEASED,
  UPCOMING
} from '@/constants/bankTransactions'
import { computed } from 'vue'

/**
 * Combines some useful computed properties for a bank transaction.
 *
 * @param {import('vue').Ref<BankTransactionResource>} transaction
 */
export const useBankTransaction = (transaction) => {
  /**
   * Returns true if the transaction is an income.
   */
  const isIncome = computed(() => transaction.value.type === 'income')

  /**
   * Returns true if the transaction is an expense.
   */
  const isExpense = computed(() => transaction.value.type === 'expense')

  /**
   * Returns true if the transaction is rejected.
   */
  const isRejected = computed(
    () => transaction.value.transaction_status === REJECTED
  )

  /**
   * Returns true if the transaction is canceled.
   */
  const isCanceled = computed(
    () => transaction.value.transaction_status === CANCELED
  )

  /**
   * Returns true if the transaction is upcoming.
   */
  const isUpcoming = computed(
    () => transaction.value.transaction_status === UPCOMING
  )

  /**
   * Returns true if the transaction is booked.
   */
  const isBooked = computed(
    () => transaction.value.transaction_status === BOOKED
  )

  /**
   * Returns true if the transaction is released.
   */
  const isReleased = computed(
    () => transaction.value.transaction_status === RELEASED
  )

  /**
   * Returns true if the transaction is pending.
   */
  const isPending = computed(
    () => transaction.value.transaction_status === PENDING
  )

  /**
   * Returns true if the transaction can be linked.
   */
  const isAssignable = computed(() => transaction.value.can_link)

  /**
   * Returns true if the transaction can be deleted.
   */
  const isDeletable = computed(() => transaction.value.can_delete)

  /**
   * Returns the detail of the transaction.
   */
  const detail = computed(
    () =>
      transaction.value.message ??
      transaction.value.reference ??
      transaction.value.reference_number
  )

  /**
   * Returns true if the transaction can be recreated.
   */
  const canRecreate = computed(
    () => isRejected.value || isCanceled.value || isReleased.value
  )

  /**
   * Returns true if the transaction can be cancelled.
   */
  const canCancel = computed(() => isUpcoming.value)

  return {
    isIncome,
    isExpense,
    isRejected,
    isCanceled,
    isAssignable,
    isBooked,
    isDeletable,
    isUpcoming,
    isReleased,
    isPending,
    detail,
    canRecreate,
    canCancel
  }
}

export default { useBankTransaction }
